import type { ApiResponse, GetListResponse } from './generic';

export interface Company {
  id: string;
  legalName: string;
  logo?: string | null;
  abbreviatedName: string;
  /** The company's own website, linked from its careers page. */
  websiteUrl?: string | null;
  createdAt: string;
  language: string;
  currency: string;
  timezone: string;
  createdByUser: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isMultiBranchEnabled: boolean;
  /** Active headcount. Only `GET /auth/linked-companies` returns it. */
  employeeCount?: number;
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  INR = 'INR',
  JPY = 'JPY',
  CNY = 'CNY',
  AUD = 'AUD',
  CAD = 'CAD',
  CHF = 'CHF',
  SGD = 'SGD',
  HKD = 'HKD',
  NZD = 'NZD',
  SEK = 'SEK',
  NOK = 'NOK',
  DKK = 'DKK',
  ZAR = 'ZAR',
  AED = 'AED',
  SAR = 'SAR',
  MXN = 'MXN',
  BRL = 'BRL',
}
/** Currencies offered for company accounts and billing. Historical payroll enums remain supported. */
export const COMPANY_CURRENCIES = [
  Currency.AUD,
  Currency.CAD,
  Currency.EUR,
  Currency.GBP,
  Currency.USD,
] as const;
export type CompanyCurrency = (typeof COMPANY_CURRENCIES)[number];

/** The currency used when a company has none on file. KollabHR is Canadian. */
export const DEFAULT_CURRENCY: CompanyCurrency = Currency.CAD;

/**
 * The currency a new amount (salary, bonus, offer, asset cost) starts in:
 * the company's own currency when `allowed` offers it, else CAD. Never USD
 * by accident.
 */
export const defaultCurrencyFor = (
  companyCurrency?: string | null,
  allowed: readonly string[] = COMPANY_CURRENCIES
): string => {
  const code = companyCurrency?.trim().toUpperCase();
  return code && allowed.includes(code) ? code : DEFAULT_CURRENCY;
};

/**
 * The currencies a picker offers: `allowed`, plus the record's own currency
 * when the list lacks it, so editing an old record never changes its
 * currency silently.
 */
export const currencyChoices = (
  current?: string | null,
  allowed: readonly string[] = COMPANY_CURRENCIES
): string[] =>
  current && !allowed.includes(current) ? [...allowed, current] : [...allowed];

export enum Language {
  EN = 'en',
  FR = 'fr',
}

/**
 * What a company is to us (JP 2026-10-21), set by a super admin. Internal
 * (real use, not paying) and test companies are left out of every platform
 * stat. Mirrors the backend enum.
 */
export enum CompanyAccountCategory {
  CUSTOMER = 'customer',
  INTERNAL = 'internal',
  TEST = 'test',
  /** A new signup the documented rules flagged, waiting for a super admin. */
  PENDING_REVIEW = 'pending_review',
  /** A signup a super admin confirmed as junk or a bot. */
  SPAM = 'spam',
}

/** One rule that raised a signup's suspicion score (super admin only). */
export interface SignupSuspicionReason {
  rule: string;
  points: number;
  detail?: string;
}

/** Why a signup was flagged, and the super admin's verdict. Never shown to a company. */
export interface SignupReviewInfo {
  signupSuspicionScore?: number | null;
  signupSuspicionReasons?: SignupSuspicionReason[] | null;
  signupScoredAt?: string | null;
  signupReviewedAt?: string | null;
}

/** How a company came to us (super admin only). */
export enum CompanyAcquisitionPath {
  SELF_SERVE = 'self_serve',
  SALES_ASSISTED = 'sales_assisted',
  PARTNER_REFERRAL = 'partner_referral',
}

/**
 * Where a company stands commercially (super admin only). Design partners and
 * trials are customers, counted apart and left out of sales cycle, conversion
 * and CAC-style numbers until they convert.
 */
export enum CompanyCommercialStatus {
  PAYING = 'paying',
  TRIAL = 'trial',
  DESIGN_PARTNER = 'design_partner',
  FREE = 'free',
}

/** Sales scope of a company (super admin only). */
export interface CommercialInfo {
  acquisitionPath?: CompanyAcquisitionPath;
  commercialStatus?: CompanyCommercialStatus;
  /** When a trial or design-partner pilot ends. */
  trialEndsAt?: string | null;
}

/** Companies per category (super admin), printed beside every headline number. */
export interface CompanyCategoryCounts {
  customers: number;
  internal: number;
  test: number;
  spam: number;
  pendingReview: number;
}

/** The companies list filter: one category, or every company. */
export const COMPANY_CATEGORY_ALL = 'all';
export type CompanyCategoryFilter =
  CompanyAccountCategory | typeof COMPANY_CATEGORY_ALL;

/** Seven UTC calendar days, oldest first, including today. */
export interface CompanyDailyActivity {
  date: string;
  admin: number;
  employee: number;
}

/** Company-detail trend range, in UTC calendar days including today. */
export type CompanyTrendDays = 7 | 30 | 90;

export interface CompanyTrendPoint {
  date: string;
  adminActions: number;
  employeeActions: number;
  employeeCount: number;
}

export interface CompanyTrends {
  days: CompanyTrendDays;
  timezone: 'UTC';
  points: CompanyTrendPoint[];
  totals: { adminActions: number; employeeActions: number };
  /** Current total employee records, including active and inactive employees. */
  employeeCount: number;
}

export interface CompanyList extends SignupReviewInfo, CommercialInfo {
  id: string;
  legalName: string;
  abbreviatedName: string;
  email: string | null;
  status: boolean;
  logo: string | null;
  language: string;
  currency: string;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  /** Employees with an active status. */
  activeEmployeeCount: number;
  /** Latest non-login action by a company admin, or null if none yet. */
  lastAdminActionAt: string | null;
  /** Latest action by an employee or manager (logins included), or null. */
  lastEmployeeActionAt: string | null;
  /** Actions by anyone at the company (admins included) in the last 7 days. */
  actionsLast7Days: number;
  /** Daily actions for the two activity sparklines; absent on older servers. */
  dailyActivity?: CompanyDailyActivity[];
  /** Customer, internal, test, pending review or spam (super admin only). */
  accountCategory?: CompanyAccountCategory;
}

export type GetCompaniesResponse = GetListResponse<CompanyList>;

/** Super admin companies list sort keys (mirrors the backend `CompanySortBy`). */
export enum CompanySortBy {
  NAME = 'name',
  CREATED_AT = 'createdAt',
  ACTIVE_EMPLOYEE_COUNT = 'activeEmployeeCount',
  LAST_ADMIN_ACTION_AT = 'lastAdminActionAt',
  LAST_EMPLOYEE_ACTION_AT = 'lastEmployeeActionAt',
  ACTIONS_LAST_7_DAYS = 'actionsLast7Days',
  STATUS = 'status',
}

export enum CompanySortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/**
 * Company admin activity buckets (logins excluded), shared by the companies
 * list filter and the dashboard's deep links. Mirrors the backend enum.
 */
export enum CompanyActivityFilter {
  /** An admin action in the last 7 days. */
  ACTIVE_7D = 'active_7d',
  /** An admin action in the last 30 days (includes the last 7). */
  ACTIVE_30D = 'active_30d',
  /** Had admin activity, but none in the last 30 days. */
  INACTIVE_30D = 'inactive_30d',
  /** No admin action ever recorded. */
  NEVER = 'never',
}

export interface GetCompaniesParams {
  page?: number;
  limit?: number;
  status?: boolean | undefined;
  search?: string;
  isActive?: boolean;
  sortBy?: CompanySortBy;
  sortOrder?: CompanySortOrder;
  language?: Language;
  activity?: CompanyActivityFilter;
  /** Joined on or after, `YYYY-MM-DD`. */
  joinedFrom?: string;
  /** Joined on or before, `YYYY-MM-DD`. */
  joinedTo?: string;
  /** Account category to list; `all` = every company. Default: customers. */
  category?: CompanyCategoryFilter;
}

export interface CompanyOverview extends SignupReviewInfo, CommercialInfo {
  companySize?: string | null;
  industry?: string | null;
  isOnboardingCompleted?: boolean;
  /**
   * Manager access (My team, manager approvals). On for every company since
   * D44; a super admin can turn it off as a kill switch.
   */
  managerAccessEnabled?: boolean;
  /**
   * Leave years (leave entitlement cycles): work-anniversary and upfront
   * leave years. Off by default; a super admin turns it on per company.
   */
  leaveCyclesEnabled?: boolean;
  /**
   * Customer (every new company), internal, test, pending review or spam.
   * Everything but customer is left out of every platform stat; only a super
   * admin sets it (a flagged new signup starts as pending review).
   */
  accountCategory?: CompanyAccountCategory;
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };

  id: string;
  legalName: string;
  abbreviatedName: string;
  /** The company's own website, or null when none is set. */
  websiteUrl: string | null;
  email: string | null;
  status: boolean;
  language: string;
  currency: string;
  timezone: string;
  isMultiBranchEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Branch {
  id: string;
  name: string;
  phoneNumber: string | null;
  email: string | null;
  branchCode: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeCount {
  companyId: string;
  companyName: string;
  totalEmployees: number;
  activeEmployees: number;
  inactiveEmployees: number;
  activePercentage: number;
}
export interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  gender: string | null;
  phoneNumber: string | null;
  email: string;
}

export interface CompanyAdmin {
  id: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
  user: AdminUser;
}

export interface CompanyOverviewData {
  company: CompanyOverview;
  addresses: CompanyAddress[];
  counts?: { addresses: number; branches: number; admins: number };
  lastAdminActionAt?: string | null;
  branches: Branch[];
  admins: CompanyAdmin[];
  employeeCount: EmployeeCount;
}

export type GetCompanyOverviewResponse = ApiResponse<CompanyOverviewData>;

export interface UpdateCompanyStatusPayload {
  status: boolean;
}

/** Super-admin edit of a company's website; blank or null clears it. */
export interface UpdateCompanyWebsitePayload {
  websiteUrl: string | null;
}

export interface CompanyStatusResponse {
  id: string;
  legalName: string;
  abbreviatedName: string;
  email: string | null;
  status: boolean;
  logo: string | null;
  language: string;
  currency: string;
  timezone: string;
  isMultiBranchEnabled: boolean;
  extraData: unknown | null;
  createdByUserId: string;
  updatedByUserId: string;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type UpdateCompanyStatusResponse = ApiResponse<CompanyStatusResponse>;

export interface CompanyAddressCountry {
  code: string;
  name: string;
}

export interface CompanyAddressProvince {
  id: number;
  name: string;
  countryCode: string;
  code: string;
}

export interface CompanyAddress {
  id: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  provinceId: number | null;
  postalCode: string;
  countryCode: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
  country: CompanyAddressCountry | null;
  province: CompanyAddressProvince | null;
}

export type GetCompanyAddressesResponse = GetListResponse<CompanyAddress>;

export enum OnboardingFeatureKey {
  Timesheet = 'timesheet',
  Assets = 'assets',
  Workflow = 'workflow',
  Files = 'files',
  Notes = 'notes',
  Leave = 'leave',
  Recruiting = 'recruiting',
  Training = 'training',
  Performance = 'performance',
}

export type CompanyFeatureFlags = Record<OnboardingFeatureKey, boolean>;

export interface CompanyFeatureStatus {
  /** Which modules the portal shows. A company that never chose reads all on. */
  featureFlags: CompanyFeatureFlags;
  /**
   * Whether timesheets actually run (clock-in, recurring jobs). Differs from
   * `featureFlags.timesheet` only for a company that never chose its modules.
   */
  timesheetActive: boolean;
}

export interface UpdateCompanyFeaturesPayload {
  features: OnboardingFeatureKey[];
}

export type GetCompanyFeaturesResponse = ApiResponse<CompanyFeatureStatus>;

export type UpdateCompanyFeaturesResponse = ApiResponse<CompanyFeatureStatus>;

/** Editable contact fields; Stripe references and masked card metadata are read-only. */
export interface CompanyBillingContact {
  billingName: string | null;
  billingEmail: string | null;
  billingPhone: string | null;
  currency: CompanyCurrency | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  countryCode: string | null;
}

export interface CompanyBillingProfile extends CompanyBillingContact {
  companyId: string;
  stripeCustomerId: string | null;
  stripePaymentMethodId: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
  stripeSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateCompanyProfilePayload {
  legalName: string;
  abbreviatedName: string;
  email: string | null;
  websiteUrl: string | null;
  currency: CompanyCurrency;
  language: Language;
  timezone: string;
}

export interface UpdateCompanyPrimaryAddressPayload {
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  provinceId: number | null;
  postalCode: string;
  countryCode: string;
}

/** Super-admin kill switch for manager access (D44). */
export interface UpdateCompanyManagerAccessPayload {
  enabled: boolean;
}

/** `PATCH /admin/companies/:companyId/leave-cycles` (super admin). */
export interface UpdateCompanyLeaveCyclesPayload {
  enabled: boolean;
}

/** `PATCH /admin/companies/:companyId/commercial` (super admin). Send only what changes; `trialEndsAt: null` clears the date. */
export type UpdateCompanyCommercialPayload = CommercialInfo;

/** `PATCH /admin/companies/:companyId/signup-review` (super admin): one click on a flagged signup. */
export type SignupReviewDecision = 'real' | 'spam';
export interface ReviewCompanySignupPayload {
  decision: SignupReviewDecision;
}

/** `PATCH /admin/companies/:companyId/category` (super admin). */
export interface UpdateCompanyCategoryPayload {
  category: CompanyAccountCategory;
}
